import { buildStageDoneSummary, type StageDoneSummary } from './stageDoneSummary';
import type { SessionTree } from '../../types/tree';
import type { WorkflowContainerRuntimeNode, WorkflowContainerTreeState } from '../../types/workflowContainer';
import type { WorkflowSessionState } from '../../types/workflowSession';

export function containersByLoopSessionId(
  tree: WorkflowContainerTreeState,
): Map<string, WorkflowContainerRuntimeNode> {
  const map = new Map<string, WorkflowContainerRuntimeNode>();
  for (const node of Object.values(tree.containersById)) {
    if (node.loopSessionId) map.set(node.loopSessionId, node);
  }
  return map;
}

function hasRunningParallelSibling(
  node: WorkflowContainerRuntimeNode,
  tree: WorkflowContainerTreeState,
): boolean {
  if (node.scope !== 'item' || !node.parent_container_id) return false;
  for (const sibling of Object.values(tree.containersById)) {
    if (sibling.container_id === node.container_id) continue;
    if (sibling.scope !== 'item') continue;
    if (sibling.parent_container_id !== node.parent_container_id) continue;
    if (sibling.status === 'running' || sibling.status === 'retrying') return true;
  }
  return false;
}

function collectProtectedLoopSessionIds(
  state: WorkflowSessionState,
  pinnedLoopSessionIds: Record<string, true>,
): Set<string> {
  const protectedIds = new Set<string>(Object.keys(pinnedLoopSessionIds));
  const byLoop = containersByLoopSessionId(state.containerTree);

  if (state.activeLoopSessionId) {
    const activeContainer = byLoop.get(state.activeLoopSessionId);
    if (
      activeContainer
      && (activeContainer.status === 'running' || activeContainer.status === 'retrying')
    ) {
      protectedIds.add(state.activeLoopSessionId);
    }
  }

  for (const containerId of state.containerTree.activeContainerIds) {
    const node = state.containerTree.containersById[containerId];
    if (!node?.loopSessionId) continue;
    if (node.status === 'running' || node.status === 'retrying') {
      protectedIds.add(node.loopSessionId);
    }
  }

  for (const node of Object.values(state.containerTree.containersById)) {
    if (!node.loopSessionId) continue;
    if (node.status === 'running' || node.status === 'retrying') {
      protectedIds.add(node.loopSessionId);
    }
    // 并行组内 sibling 仍 running 时，保留已完成 item 的 loop 树（避免只见 teaser 不见 tool）
    if (
      (node.status === 'completed' || node.status === 'failed')
      && hasRunningParallelSibling(node, state.containerTree)
    ) {
      protectedIds.add(node.loopSessionId);
    }
  }

  return protectedIds;
}

/**
 * P2：淘汰 completed/failed 且未 pin/不在热缓存中的 loop 树，缓存 done 摘要。
 * hotCompletedLoopSessionIds 中的 completed loop 受保护（LRU 热缓存）。
 */
export function evictInactiveLoopTrees(
  state: WorkflowSessionState,
  pinnedLoopSessionIds: Record<string, true>,
): WorkflowSessionState {
  const protectedIds = collectProtectedLoopSessionIds(state, pinnedLoopSessionIds);

  // P2：热缓存中的 completed loop 也受保护
  for (const id of state.hotCompletedLoopSessionIds) {
    protectedIds.add(id);
  }

  const byLoop = containersByLoopSessionId(state.containerTree);
  const nextLoops: Record<string, SessionTree> = { ...state.loopTreesBySessionId };
  const nextSummaries: Record<string, StageDoneSummary> = {
    ...state.stageDoneSummariesByContainerId,
  };

  for (const [loopSessionId, tree] of Object.entries(state.loopTreesBySessionId)) {
    if (protectedIds.has(loopSessionId)) continue;

    const container = byLoop.get(loopSessionId);
    if (!container) continue;
    if (container.status !== 'completed' && container.status !== 'failed') continue;

    const summary = buildStageDoneSummary(tree);
    if (summary) {
      nextSummaries[container.container_id] = summary;
    }
    delete nextLoops[loopSessionId];
  }

  return {
    ...state,
    loopTreesBySessionId: nextLoops,
    stageDoneSummariesByContainerId: nextSummaries,
  };
}

/**
 * P2：将 completed loop 移到 LRU 热缓存尾部（最近使用）。
 * 如果该 loop 尚未在内存中（尚未 hydrate），仍然 touch 以预占位置。
 */
export function touchCompletedLoopSession(
  state: WorkflowSessionState,
  loopSessionId: string,
): WorkflowSessionState {
  const ids = state.hotCompletedLoopSessionIds.filter((id) => id !== loopSessionId);
  ids.push(loopSessionId); // 最近使用在尾部
  return {
    ...state,
    hotCompletedLoopSessionIds: ids,
  };
}

/**
 * P2：热缓存溢出淘汰 — 超过 maxHydratedCompletedLoops 时，
 * 从头部（最旧）淘汰 completed loop 树，但保留 done 摘要。
 */
export function evictCompletedLoopOverflow(
  state: WorkflowSessionState,
  maxHydratedCompletedLoops: number,
): WorkflowSessionState {
  if (state.hotCompletedLoopSessionIds.length <= maxHydratedCompletedLoops) {
    return state;
  }

  const overflow = state.hotCompletedLoopSessionIds.length - maxHydratedCompletedLoops;
  const candidateEvictIds = state.hotCompletedLoopSessionIds.slice(0, overflow);
  let trimmedIds = state.hotCompletedLoopSessionIds.slice(overflow);

  const byLoop = containersByLoopSessionId(state.containerTree);
  const nextLoops: Record<string, SessionTree> = { ...state.loopTreesBySessionId };
  const nextSummaries: Record<string, StageDoneSummary> = {
    ...state.stageDoneSummariesByContainerId,
  };

  for (const id of candidateEvictIds) {
    const tree = nextLoops[id];
    if (!tree) continue;
    const container = byLoop.get(id);
    // 并行 sibling 仍 running 时不淘汰已完成 item 的 loop（与 evictInactiveLoopTrees 同构）
    if (
      container
      && hasRunningParallelSibling(container, state.containerTree)
    ) {
      if (!trimmedIds.includes(id)) {
        trimmedIds = [id, ...trimmedIds];
      }
      continue;
    }
    if (container) {
      const summary = buildStageDoneSummary(tree);
      if (summary) {
        nextSummaries[container.container_id] = summary;
      }
    }
    delete nextLoops[id];
  }

  return {
    ...state,
    loopTreesBySessionId: nextLoops,
    stageDoneSummariesByContainerId: nextSummaries,
    hotCompletedLoopSessionIds: trimmedIds,
  };
}
